import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { steeringService } from 'src/services/AI/steering.service';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { tSteeringInstruction, tUseSLBDirectorPanelArgs } from './SLBDirectorPanel.types';

export function useSLBDirectorPanel({ chatId, workspaceId }: tUseSLBDirectorPanelArgs) {
  const { t } = useTranslation('chat/chat');
  const { addAlert } = useToastContext();
  const [instructions, setInstructions] = useState<tSteeringInstruction[]>([]);
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // True once the mount-time silent fetch has resolved, so the UI can tell
  // "still loading" apart from "confirmed empty" and avoid flashing the empty
  // state before the active instructions arrive.
  const [isInitialized, setIsInitialized] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch instructions on mount and when chat changes — so the panel always
  // shows current state without requiring the user to click "Activas".
  useEffect(() => {
    setInstructions([]);
    setText('');
    setConfirmation(null);
    setIsInitialized(false);
    if (chatId && workspaceId) {
      void fetchInstructionsSilent();
    } else {
      setIsInitialized(true);
    }
  }, [chatId]);

  // Silent fetch — same as fetchInstructions but doesn't show loading or error toast.
  // Used on mount to pre-populate the count badge without blocking the UI.
  async function fetchInstructionsSilent() {
    try {
      const data = await steeringService.getInstructions(workspaceId, chatId);
      setInstructions(data ?? []);
    } catch {
      // Silent — don't show error on mount, the user hasn't asked for instructions yet
    } finally {
      setIsInitialized(true);
    }
  }

  // Clean up the confirmation auto-dismiss timer on unmount.
  useEffect(() => {
    return () => {
      if (confirmationTimerRef.current) clearTimeout(confirmationTimerRef.current);
    };
  }, []);

  // Lazy — only called when the user opens the "Activas" tab, not on mount.
  async function fetchInstructions() {
    if (!chatId || !workspaceId) return;
    setIsLoading(true);
    try {
      const data = await steeringService.getInstructions(workspaceId, chatId);
      setInstructions(data ?? []);
    } catch {
      setInstructions([]);
      addAlert({ type: eAlertType.ERROR, message: t('slbDirectorPanel.errorFetch') });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;
    setIsSending(true);
    try {
      const created = await steeringService.appendSteering(workspaceId, chatId, trimmed);
      setInstructions((prev) => [...prev, created]);
      setText('');
      setConfirmation(trimmed);
      if (confirmationTimerRef.current) clearTimeout(confirmationTimerRef.current);
      confirmationTimerRef.current = setTimeout(() => setConfirmation(null), 5000);
    } catch {
      addAlert({ type: eAlertType.ERROR, message: t('slbDirectorPanel.errorSend') });
    } finally {
      setIsSending(false);
    }
  }

  async function handleDelete(instructionId: string) {
    setIsDeleting(true);
    try {
      await steeringService.deleteInstruction(workspaceId, chatId, instructionId);
      setInstructions((prev) => prev.filter((i) => i.id !== instructionId));
    } catch {
      addAlert({ type: eAlertType.ERROR, message: t('slbDirectorPanel.errorDelete') });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleClearAll() {
    setIsDeleting(true);
    try {
      await steeringService.clearAll(workspaceId, chatId);
      setInstructions([]);
    } catch {
      addAlert({ type: eAlertType.ERROR, message: t('slbDirectorPanel.errorClearAll') });
    } finally {
      setIsDeleting(false);
    }
  }

  return {
    instructions,
    text,
    setText,
    isSending,
    isDeleting,
    isLoading,
    isInitialized,
    confirmation,
    fetchInstructions,
    handleSend,
    handleDelete,
    handleClearAll,
  };
}
